import { useDataExplorerAppearance } from './appearanceContext';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';

import type { DataExplorerSelectProps } from './types';
import { resolveDataExplorerAppearance } from './theme';
import { cn } from './utils';

function ChevronDownIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      className="h-4 w-4"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
    >
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      className="h-4 w-4"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
    >
      <path d="m5 12 4 4L19 6" />
    </svg>
  );
}

export function DataExplorerSelect({
  value = '',
  options = [],
  label,
  placeholder = 'Select',
  icon,
  title,
  triggerLabel,
  disabled = false,
  display = 'value',
  align = 'start',
  sideOffset = 6,
  appearance,
  className,
  classNames,
  style,
  onValueChange,
}: DataExplorerSelectProps) {
  const theme = resolveDataExplorerAppearance(
    useDataExplorerAppearance(appearance),
  );
  const selected = options.find((option) => option.value === value);
  const valueNode =
    display === 'label-value' && label ? (
      <>
        <span>{label}</span>
        <span className="text-muted-foreground">
          {selected?.label ?? placeholder}
        </span>
      </>
    ) : (
      selected?.label ?? label ?? placeholder
    );

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          title={title}
          aria-label={triggerLabel}
          disabled={disabled || options.length === 0}
          className={cn(
            theme.slots.controlSelectTrigger,
            classNames?.trigger,
            classNames?.root,
            className,
          )}
          style={{ ...theme.styles.control, ...style }}
          onClick={(event) => event.stopPropagation()}
        >
          {icon ? (
            <span className={cn(theme.slots.controlButtonIcon, classNames?.icon)}>
              {icon}
            </span>
          ) : null}
          <span
            className={cn(theme.slots.controlSelectValue, classNames?.value)}
          >
            {valueNode}
          </span>
          <ChevronDownIcon />
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align={align}
          sideOffset={sideOffset}
          className={cn(theme.slots.menuContent, classNames?.content)}
          style={theme.styles.menuContent}
          onClick={(event) => event.stopPropagation()}
        >
          <DropdownMenu.RadioGroup
            value={value}
            onValueChange={(nextValue) => {
              if (disabled) return;
              void onValueChange?.(nextValue);
            }}
          >
            {options.map((option) => (
              <DropdownMenu.RadioItem
                key={option.value}
                value={option.value}
                title={option.title}
                disabled={option.disabled}
                className={cn(theme.slots.menuItem, classNames?.item)}
                style={theme.styles.menuItem}
              >
                <span className={cn(theme.slots.menuItemIcon, classNames?.itemIcon)}>
                  {option.value === value ? <CheckIcon /> : option.icon}
                </span>
                <span className={cn(theme.slots.menuItemLabel, classNames?.itemLabel)}>
                  {option.label}
                </span>
              </DropdownMenu.RadioItem>
            ))}
          </DropdownMenu.RadioGroup>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
